import { ACTIVITY_PING_MS, IDLE_LOGOUT_MS, IDLE_WARNING_MS } from './config';
import { ACTIVE_VIDEO_STATUSES } from '@/lib/schemas/video.schema';

// La logique de l'inactivite (CNX), sans navigateur : ActivityTracker ne fait que brancher les evenements.

export type IdleState = { show: 'none' | 'warning' | 'logout' | 'expired'; secondsLeft: number };

/**
 * Au-dela de la deconnexion d'autant, l'appareil dormait : la session a expire sans nous. Plus large que le
 * ralentissement des minuteries d'un onglet en arriere-plan (environ une minute dans Chrome).
 */
export const EXPIRED_AFTER_MS = 3 * 60_000;

/** Un verrou de deconnexion plus vieux que cela est abandonne (onglet ferme en route) */
export const LOCK_STALE_MS = 30_000;

/** L'etat de l'inactivite, d'apres la derniere activite partagee entre les onglets */
export function idleState(now: number, lastActivity: number): IdleState {
  const idle = now - lastActivity;
  if (idle >= IDLE_LOGOUT_MS + EXPIRED_AFTER_MS) return { show: 'expired', secondsLeft: 0 };
  if (idle >= IDLE_LOGOUT_MS) return { show: 'logout', secondsLeft: 0 };
  if (idle >= IDLE_WARNING_MS) return { show: 'warning', secondsLeft: Math.ceil((IDLE_LOGOUT_MS - idle) / 1000) };
  return { show: 'none', secondsLeft: Math.ceil((IDLE_LOGOUT_MS - IDLE_WARNING_MS) / 1000) };
}

/** Signaler l'activite au serveur : apres une activite dans cet onglet, au plus une fois par minute */
export function shouldPing({ now, localActivity, lastPing }: { now: number; localActivity: number; lastPing: number }): boolean {
  return localActivity > lastPing && now - lastPing >= ACTIVITY_PING_MS;
}

export type LogoutLock = { owner: string; at: number } | null;

/** Un seul onglet mene la deconnexion : celui qui tient le verrou, ou qui reprend un verrou abandonne */
export function canTakeLock(lock: LogoutLock, me: string, now: number): boolean {
  return !lock || lock.owner === me || now - lock.at > LOCK_STALE_MS;
}

// Un rendu est en cours tant qu'il attend ou se calcule ; une video, selon ses propres statuts
const ACTIVE_JOB_STATUSES = new Set<string>(['en_attente', 'en_cours', ...ACTIVE_VIDEO_STATUSES]);

/** Les rendus et videos en cours connus de l'editeur, d'apres les evenements en direct */
export function trackBackgroundJobs(running: Set<string>, event: { id: string; status: string }): Set<string> {
  const next = new Set(running);
  if (ACTIVE_JOB_STATUSES.has(event.status)) next.add(event.id);
  else next.delete(event.id);
  return next;
}

/** Le temps laisse aux onglets pour garder leur travail avant la fin de la session */
export const DRAFT_WAIT_MS = 2000;

/** saved : en base ; local : dans ce navigateur seulement ; lost : ni l'un ni l'autre (serveur et stockage) */
export type DraftResult = 'saved' | 'local' | 'lost' | 'none';

/** Ce que dit la page de connexion : travail garde sur le serveur, dans ce navigateur seulement, perdu, ou rien */
export function combineDraftResults(results: DraftResult[]): 'garde' | 'navigateur' | 'perdu' | null {
  if (results.includes('lost')) return 'perdu';
  if (results.includes('local')) return 'navigateur';
  if (results.includes('saved')) return 'garde';
  return null;
}
